import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Collapse, { Panel } from 'rc-collapse'
import { CollapsePanelProps } from 'rc-collapse/lib/interface'
import { useSelector } from 'react-redux'

import { H5 } from '@ui/blocks/Heading'
import { ROUTES } from '@app/constants'
import ArrowDownIcon from '@app/ui/assets/icons/ArrowDownIcon'
import { segmentEventsNames, trackSegmentEvent } from '@app/utils/analytics'
import { getSessionIdSelector } from '@app/store/session'

import { ZipCodeForm } from '../../ZipCodeForm'
import { PARENTS_URL } from './data'

interface MobileLayoutWrapperProps {
  topOffset: number
  isOpen: boolean
  activeKey?: string | undefined
}

const MobileMenuOffsetLeft = '5.5rem'
const MobileMenuOffsetRight = '6.5rem'

const MobileLayoutWrapper = styled.section<MobileLayoutWrapperProps>`
  transition: opacity 0.3s ease-out, max-height 0.3s ease-out;
  display: flex;
  ${({ isOpen }) => (isOpen ? 'opacity: 1; max-height: 150rem;' : 'opacity: 0; max-height: 0;')}
  flex-flow: column;
  align-items: center;
  top: ${({ topOffset }) => topOffset}rem;
  bottom: 0;
  left: 0;
  right: 0;
  position: fixed;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - ${({ topOffset }) => topOffset}rem);
  background-color: ${({ theme }) => theme.global.colors.floralWhite};

  .mobile-menu-wrapper {
    width: 100%;
    margin-bottom: 3.6rem;
  }

  .rc-collapse-header {
    padding: 2rem 0;
    display: flex;
    align-items: center;
    margin: 0 ${MobileMenuOffsetRight} 0 ${MobileMenuOffsetLeft};

    a {
      color: ${({ theme }) => theme.global.colors.elemyGreen};
    }
  }

  .rc-collapse-content {
    transition: all 0.3s ease-out;
    opacity: 1;
    max-height: 50rem;

    a {
      color: ${({ theme }) => theme.global.colors.genoa};

      :hover {
        color: ${({ theme }) => `${theme.global.colors.elemyGreen}`};
      }

      :active {
        color: ${({ theme }) => `${theme.global.colors.genoa}`};
      }
    }
  }

  .rc-collapse-content-hidden {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
  }

  .mobile-menu-item {
    .rc-collapse-header {
      border-bottom: 0.1rem solid ${({ theme }) => theme.global.colors.tiara};
      cursor: pointer;
    }

    &.rc-collapse-item-active {
      .rc-collapse-header {
        border-bottom: none;
      }
    }

    :last-child {
      .rc-collapse-header {
        border-bottom: none;
      }
    }

    ul {
      padding: 1.6rem 0;
      background-color: rgba(201, 212, 212, 0.2);
    }

    li {
      padding: 0.8rem ${MobileMenuOffsetRight} 0.8rem ${MobileMenuOffsetLeft};
    }
  }

  @media ${({ theme }) => theme.device.mobileL} {
    .rc-collapse-header {
      padding: 2rem 0;
      margin: 0 2rem;
    }

    .mobile-menu-item {
      li {
        padding: 0.8rem 2rem;
      }
    }
  }
`

const MobileZipForm = styled(ZipCodeForm)`
  width: 100%;
  padding: 0 4.8rem;
  margin-bottom: 3.6rem;
  max-width: 28rem;
  align-self: start;

  .zip-code-form {
    flex-wrap: initial;
  }

  .zip-code-input {
    margin-right: 0.8rem;
    margin-bottom: 0;
  }

  .zip-code-button {
    width: auto;
    margin-right: 0;
  }

  @media ${({ theme }) => theme.device.mobileL} {
    max-width: 100%;
    padding: 0 2rem;

    .zip-code-form {
      flex-wrap: wrap;
    }

    .zip-code-input {
      margin-right: 0;
      margin-bottom: 2.4rem;
    }

    .zip-code-button {
      width: 100%;
    }
  }
`

const StyledArrowDownIcon = styled(ArrowDownIcon)`
  margin-left: auto;
  order: 2;
`

const expandIcon = ({ isActive }: Partial<CollapsePanelProps>) => <StyledArrowDownIcon inversedVertical={isActive} />

const MobileMenuLayout = ({ topOffset = 0, isOpen = false }: MobileLayoutWrapperProps) => {
  const sessionId = useSelector(getSessionIdSelector)
  const [activeKey, setActiveKey] = useState('')
  const handleChange = useCallback(
    (activeKey: string) => {
      setActiveKey(activeKey)
    },
    [setActiveKey]
  )

  const handleClickLogin = () => {
    if (sessionId) {
      trackSegmentEvent(sessionId, segmentEventsNames.PARENT360_LOG_IN)
    }
  }

  return (
    <MobileLayoutWrapper topOffset={topOffset} isOpen={isOpen} activeKey={activeKey}>
      <Collapse
        onChange={handleChange}
        accordion
        activeKey={activeKey}
        className="mobile-menu-wrapper"
        expandIcon={expandIcon}>
        <Panel
          showArrow={false}
          forceRender
          key="Login Link"
          className="mobile-menu-item"
          header={
            <a onClick={handleClickLogin} href={PARENTS_URL} target="_blank" rel="noreferrer nofollow noopener">
              Login
            </a>
          }
        />
        <Panel
          forceRender
          key="For Parents"
          className="mobile-menu-item"
          header="For Parents"
          headerClass="my-header-class-parents">
          <ul>
            <H5 as="li">
              <Link to={ROUTES.abaTherapy}>ABA Therapy</Link>
            </H5>
            <H5 as="li">
              <Link to={ROUTES.autismDiagnosis}>Autism Diagnosis</Link>
            </H5>
            <li>
              <Link to={ROUTES.locations}>Our Locations</Link>
            </li>
            <li>
              <Link to={ROUTES.ourClinicians}>Our Providers</Link>
            </li>
            <li>
              <Link to={ROUTES.learningStudio}>Autism Resource Center</Link>
            </li>
            <li>
              <Link to={ROUTES.abaCostCalculator}>ABA Cost Calculator</Link>
            </li>
            <li>
              <Link to={ROUTES.faq}>FAQ</Link>
            </li>
          </ul>
        </Panel>
        <Panel
          showArrow={false}
          forceRender
          key="For ABA Clinicians"
          className="mobile-menu-item"
          header={<Link to={ROUTES.joinUs}>For ABA Clinicians</Link>}
        />
        <Panel
          showArrow={false}
          forceRender
          key="For Pediatricians"
          className="mobile-menu-item"
          header={<Link to={ROUTES.forPediatricians}>For Pediatricians</Link>}
        />
        <Panel
          forceRender
          key="Company"
          className="mobile-menu-item"
          header="Company"
          headerClass="my-header-class-company">
          <ul>
            <li>
              <Link to={ROUTES.ourStory}>Our Story</Link>
            </li>
            <li>
              <Link to={ROUTES.ourClinicians}>Our Providers</Link>
            </li>
            <li>
              <Link to={ROUTES.corporateCareers}>Corporate Careers</Link>
            </li>
            <li>
              <Link to={ROUTES.contactUs}>Contact Us</Link>
            </li>
          </ul>
        </Panel>
      </Collapse>
      <MobileZipForm />
    </MobileLayoutWrapper>
  )
}

export default MobileMenuLayout
